// SPDX-License-Identifier: MPL-2.0

import { ja } from './ja'

export const en: Record<keyof typeof ja, string> = {
  // App
  'app.title': 'Kaguya',
  'app.tagline': 'Kaguya — A gentle Misskey client',
  'app.subtitle': 'A gentle, simple Misskey client',

  // Navigation
  'nav.home': 'Home',
  'nav.inbox': 'Inbox',
  'nav.notifications': 'Notifications',
  'nav.settings': 'Settings',
  'nav.back': 'Back',
  'nav.side_navigation': 'Side navigation',
  'nav.bottom_navigation': 'Bottom navigation',

  // Common actions
  'action.close': 'Close',
  'action.cancel': 'Cancel',
  'action.remove': 'Remove',
  'action.back': 'Back',
  'action.copy': 'Copy',
  'action.retry': 'Try again',
  'action.refresh': 'Refresh',
  'action.refresh_cooldown': 'You can refresh again in {s}s',
  'action.load_more': 'Load more',

  // Theme
  'theme.switch_to_light': 'Switch to light mode',
  'theme.switch_to_dark': 'Switch to dark mode',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  // Quiet mode
  'quiet_mode.on': 'Quiet mode',
  'quiet_mode.off': 'Disable quiet mode',
  'quiet_mode.status': 'Quiet',
  'quiet_mode.manual_active': 'Quiet mode is on — streaming is paused',
  'quiet_mode.hours_active': 'Quiet hours are in effect',
  'quiet_mode.resume': 'Resume',

  // Compose
  'compose.title': 'Write a note',
  'compose.placeholder': "What's on your mind?",
  'compose.cw_placeholder': 'Content warning annotation',
  'compose.cw_button': 'Content Warning (CW)',
  'compose.attach_image': 'Attach image',
  'compose.attachment_alt': 'Attached image',
  'compose.alt_placeholder': 'Describe the picture (for people who cannot see it). Left blank on hackers.pub, one is written for you',
  'compose.alt_required': 'Could not describe the picture. Add a description and send again',
  'compose.visibility': 'Visibility',
  'compose.language': 'Language',
  'compose.language_auto': 'Auto ({lang})',
  'compose.visibility_public': 'Public',
  'compose.visibility_home': 'Home only',
  'compose.visibility_followers': 'Followers only',
  'compose.visibility_specified': 'Direct message',

  // Timeline
  'timeline.filtered': 'Filtered',
  'timeline.filtered_active': 'Filtered',
  'compose.sending': 'Sending...',
  'compose.submit': 'Post',
  'compose.posted': 'Posted',
  'compose.reply_placeholder': 'Write a reply...',

  // Note
  'note.collapse_cw': 'Hide',
  'note.reveal_cw': 'Show',
  'note.reply': 'Reply',
  'note.renote': 'Renote',
  'note.reaction': 'Reaction',
  'note.add_reaction': 'Add reaction',
  'note.more': 'More',
  'note.open_original': 'Open original',
  'note.copy_link': 'Copy link',
  'note.link_copied': 'Link copied',
  'note.copy_failed': 'Could not copy',
  'note.from_user': "'s note",
  'note.user_renoted': ' renoted',
  'note.section_replies': 'Replies',

  // Note errors
  'note.renoted': 'Renoted',
  'note.renote_failed': 'Could not renote',
  'note.reaction_failed': 'Could not react',
  'note.post_failed': 'Could not post',
  'note.image_upload_failed': 'Image upload failed',
  'note.favorite': 'Favorite',
  'note.unfavorite': 'Unfavorite',
  'note.favorited': 'Added to favorites',
  'note.unfavorited': 'Removed from favorites',
  'note.favorite_failed': 'Could not favorite',

  // Image
  'image.sensitive_aria': 'Sensitive image, tap to show',
  'image.enlarge': 'Enlarge image',
  'image.show_sensitive': 'Show sensitive content',
  'image.sensitive_label': 'Sensitive',
  'image.tap_to_show': 'Tap to show',
  'image.viewer': 'Image viewer',
  'image.close_viewer': 'Close image viewer',

  // Timeline
  'timeline.home': 'Home',
  'timeline.local': 'Local',
  'timeline.global': 'Global',
  'timeline.hybrid': 'Social',
  'timeline.antenna': 'Antenna',
  'timeline.list': 'List',
  'timeline.channel': 'Channel',
  'timeline.feed': 'Feed',
  'timeline.streaming': 'Live',
  'timeline.loading': 'Loading...',
  'timeline.no_notes': 'No notes yet',
  'timeline.filter_no_rules': 'This timeline filters local notes by reaction count.',
  'timeline.filter_no_rules_hint': 'Add a rule in Settings \u2192 Filtered timeline to curate it.',
  'timeline.filter_hides_all': 'No notes match your filter rules right now.',
  'timeline.user_filter_hides_all': 'All fetched notes are hidden by your user filters.',
  'timeline.no_more': 'No more notes',
  'timeline.load_failed': 'Could not load',
  'timeline.reconnecting': 'Waiting for the connection…',
  'timeline.load_failed_retry': 'Could not load. Please try again',
  'timeline.caught_up': "You're all caught up",
  'timeline.new_notes': ' new notes',
  'timeline.show_new': 'Show',
  'timeline.details': 'Details',
  'timeline.what_went_wrong': 'What went wrong?',

  // Notifications
  'notifications.title': 'Notifications',
  'notifications.empty': 'No notifications yet',
  'notifications.close': 'Dismiss notification',
  'notifications.mark_all_read': 'Mark all as read',

  // Emoji
  'emoji.picker': 'Emoji picker',
  'emoji.search_placeholder': 'Search emoji...',
  'emoji.search': 'Search emoji',
  'emoji.close_picker': 'Close emoji picker',
  'emoji.all': 'All',
  'emoji.not_found': 'No emoji found',
  'emoji.list': 'Emoji list',
  'emoji.select': 'Select ',

  // Account
  'account.menu': 'Account menu',
  'account.remove': 'Remove account',
  'account.add': 'Add account',
  'account.logout': 'Log out',
  'account.invalid_tokens': 'Accounts with invalid tokens',

  // Settings
  'settings.title': 'Settings',
  'settings.section_account': 'Account',
  'settings.section_notifications': 'Notifications',
  'settings.push_notifications': 'Push notifications',
  'settings.section_posting': 'Posting',
  'settings.section_filtered_timeline': 'Filtered Timeline',
  'settings.filtered_timeline_description': 'The "Filtered" local timeline only shows notes that match all the rules below. Reactions use partial name matching (e.g. "kawaii" matches :kawaii@.:).',
  'settings.filter_logic': 'Combine rules with',
  'settings.filter_emoji': 'Reaction name',
  'settings.filter_emoji_placeholder': 'e.g. kawaii',
  'settings.filter_operator': 'Operator',
  'settings.filter_threshold': 'Count',
  'settings.filter_add_rule': 'Add rule',
  'settings.filter_no_rules': 'No rules — all local notes shown.',
  'settings.default_note_visibility': 'Default note visibility',
  'settings.default_renote_visibility': 'Default renote visibility',
  'settings.section_display': 'Display',
  'settings.font_size': 'Font size',
  'settings.font_small': 'S',
  'settings.font_medium': 'M',
  'settings.font_large': 'L',
  'settings.reduce_motion': 'Reduce motion',
  'settings.streaming': 'Live streaming',
  'settings.streaming_description': 'Update timelines automatically as new notes arrive. Off by default for a calmer feed.',
  'settings.section_quiet': 'Quiet mode',
  'settings.quiet_manual': 'Enable quiet mode now',
  'settings.quiet_manual_description': 'Pause streaming, dim the live indicator, and buffer new notes.',
  'settings.quiet_hours': 'Quiet hours',
  'settings.quiet_hours_description': 'Automatically enter quiet mode during the hours below.',
  'settings.quiet_hours_range': 'Time range',
  'settings.quiet_hours_start': 'Start time',
  'settings.quiet_hours_end': 'End time',
  'settings.media_proxy': 'Media proxy (privacy)',
  'settings.media_proxy_description': 'Load images and emoji through your instance\'s proxy to prevent IP address leakage to remote servers.',
  'settings.hide_nsfw': 'Hide NSFW content',
  'settings.hide_nsfw_description': 'Hide notes with content warnings (CW) or sensitive media from the timeline.',
  'settings.section_theme': 'Theme',
  'settings.section_language': 'Language',
  'settings.section_cache': 'Cache',
  'settings.cache_emoji': 'Cache emoji',
  'settings.cache_emoji_description': 'Download and cache all custom emoji from your instance. Emoji are loaded on demand by default.',
  'settings.cache_emoji_btn': 'Cache emoji now',
  'settings.cache_emoji_loading': 'Caching...',
  'settings.cache_emoji_done': 'Cached',
  'settings.section_data': 'Data',
  'settings.clear_all_data': 'Clear all data',
  'settings.clear_all_confirm': 'Clear all data?\nAll account information and settings will be erased. This cannot be undone.',
  'settings.reset_app': 'Clear cache',
  'settings.reset_app_description': 'For when something is stuck. Clears the old service worker and cache, then reloads. Works even if JavaScript is broken.',
  'settings.section_about': 'About this app',
  'settings.about_privacy': 'This app does not use any analytics or tracking. All account information and settings are stored only in this browser\'s localStorage and are never sent to external servers.',

  // Login
  'login.instance': 'Instance',
  'login.instance_placeholder': 'misskey.io',
  'login.access_token': 'Access token',
  'login.permission_mode': 'Permission mode',
  'login.permission_standard': 'Standard (read & write)',
  'login.permission_readonly': 'Read only',
  'login.permission_details_summary': 'About permissions',
  'login.permission_standard_detail': 'Standard (read & write): You can post notes, react, follow, and perform all operations.',
  'login.permission_readonly_detail': 'Read only: View timelines only. No risk of accidental actions.',
  'login.token_privacy': 'Tokens are stored only in this browser and are never sent externally.',
  'login.connecting': 'Connecting...',
  'login.login_with_token': 'Connect with token',
  'login.login_with_miauth': 'Log in with MiAuth',
  'login.login_with_oauth2': 'Log in with OAuth2',
  'login.help_miauth': 'You will be redirected to your instance to authenticate. No need to create a token manually.',
  'login.help_token': 'Get an access token from your instance\'s Settings → API.',
  'login.help_oauth2': 'Authenticate securely with OAuth2. If your instance doesn\'t support OAuth2, try MiAuth.',
  'login.help_mastodon': 'Authenticate securely with OAuth2.',
  'login.help_hackerspub': 'hackers.pub signs you in by email. Enter your username (or email), then the code we send you.',
  'login.hp_username': 'Username',
  'login.hp_email': 'Email',
  'login.hp_use_username': 'Username',
  'login.hp_use_email': 'Email',
  'login.hp_send_code': 'Send code',
  'login.hp_code': 'Sign-in code',
  'login.hp_code_sent': 'We emailed you a sign-in code. Enter it here.',
  'login.hp_verify': 'Verify',
  'login.hp_back': 'Use a different account',
  'login.hp_link_sent': 'We sent you an email. Tap the link in it to sign in.',
  'login.hp_enter_code': 'Enter a code instead',
  'login.login_with_bluesky': 'Log in with Bluesky',
  'login.help_bluesky': 'Enter your Bluesky handle. You will be redirected to authorize this app.',
  'login.bluesky_handle': 'Bluesky handle',
  'login.validating': 'Loading...',

  // Auth errors
  'error.invalid_credentials': 'Could not log in. Please check your instance URL and token.',
  'error.network': 'Could not connect to the server. Please check your instance URL and network connection.',
  'error.unknown': 'Something went wrong. Please try again later.',
  'error.not_connected': 'We couldn\u2019t reach the server \u2014 is your connection OK?',
  'readonly.explanation': 'You connected with a read-only token \u2014 posting, reacting, and following are disabled.',
  'error.post_failed': 'Could not post',

  // Auth callback
  'auth.checking': 'Checking authentication...',
  'auth.login_success': 'Login successful!',
  'auth.redirecting_home': 'Redirecting to home...',
  'auth.failed': 'Authentication failed.',
  'auth.error_details': 'Error details',
  'auth.back_to_login': 'Back to login',
  'auth.hp_other_browser': 'This link was opened in a different browser than the one you started signing in from.',
  'auth.hp_sign_in_here': 'Sign in on this browser',
  'auth.hp_copy_code': 'Copy the code',
  'auth.hp_copied': 'Copied',
  'auth.hp_copy_hint': 'Paste the copied code into "Enter a code instead" in the browser where you started signing in.',
  'auth.logging_in': 'Logging in...',
  'auth.session_expired': 'Session expired or not found.',
  'auth.redirecting_login': 'Redirecting to login...',

  // User page
  'user.decode_failed': 'Failed to parse user info',
  'user.stat_notes': 'Notes',
  'user.stat_following': 'Following',
  'user.stat_followers': 'Followers',
  'user.pinned_notes': 'Pinned',
  'user.section_notes': 'Notes',
  'user.no_notes': 'No notes yet',
  'user.no_more': 'No more notes',
  'user.follow': 'Follow',
  'user.unfollow': 'Unfollow',
  'user.follow_failed': 'Could not follow',
  'user.unfollow_failed': 'Could not unfollow',
  'user.followed': 'Followed',
  'user.unfollowed': 'Unfollowed',
  'user.bot_badge': 'Bot',

  // Poll
  'poll.votes': ' votes',
  'poll.expired': 'Poll ended',
  'poll.expires_at': 'Ends',
  'poll.vote_failed': 'Could not vote',
  'poll.voted': 'Voted',
  'poll.label': 'Poll',

  // Note page
  'note_page.remote_fetch_failed': 'Failed to fetch remote note',
  'note_page.remote_resolve_failed': 'Remote URI could not be resolved as a note',
  'note_page.decode_failed': 'Failed to parse note',
  'note_page.remote_warning': 'This note was fetched from a remote server. Some information may be incomplete compared to the original.',
  'note_page.view_original': 'View original note →',

  // Push notifications
  'push.unsupported': 'Push notifications not supported',
  'push.permission_denied': 'Notifications are blocked',
  'push.generating': 'Generating script...',
  'push.disable': 'Disable push notifications',
  'push.reload_retry': 'Reload page and retry',
  'push.enable': 'Enable push notifications',
  'push.script_instructions': 'Run the following AiScript in Misskey\'s Scratchpad:',
  'push.open_scratchpad': 'Open Scratchpad',
  'push.executed': 'Executed',
  'push.migrated': 'Push notifications have been migrated to native Misskey mode. Please enable from Settings.',
  'push.back_to_settings': '← Settings',

  // App state
  'app.new_version': 'A new version is available',
  'app.update_now': 'Update now',
  'app.select_note': 'Select a note',
  'app.loading': 'Loading...',

  // Image gallery
  'image.gallery_single': 'Image',
  'image.gallery_count': ' images',

  // MFM
  'mfm.search_button': 'Search',

  // User filter
  'user_filter.button': 'User filter',
  'user_filter.images_only': 'Images only',
  'user_filter.no_renotes': 'Hide renotes',

  // Inbox
  'inbox.title': 'Inbox',
  'inbox.empty': 'All clear',
  'inbox.clear_all': 'Dismiss all',
  'inbox.dismiss_group': 'Dismiss',
  'inbox.see_all': 'View all notifications',
  'inbox.group_direct': 'Mentions & Replies',
  'inbox.group_quotes': 'Quotes',
  'inbox.group_renotes': 'Renotes',
  'inbox.group_reactions': 'Reactions',
  'inbox.group_follows': 'Follows',
  'inbox.group_other': 'Other',
  'inbox.group_home': 'Home Timeline',
  'inbox.go_home': 'Go to home',
  'inbox.view_thread': 'View thread',
  'inbox.mark_read': 'Mark as read',

  // Timeline Inbox
  'timeline_inbox.title': 'Timeline Inbox',

  // Generic
  'generic.error_prefix': 'Error',

  // Interpolated
  'image.show_more_files': 'Show more ({n} file(s))',
  'aria.mention_to': 'Mention: {handle}',
  'aria.emoji_name': '{name} emoji',
}
